import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PageLayout, Button } from '../common/index.js';
import reportService from '../../services/reportService.js';
import userService from '../../services/UserService.js';
import vehicleService from '../../services/vehicleService.js';
import { locationHubService } from '../../services/locationHubService.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import {
  FileSpreadsheet,
  Download,
  Users,
  MapPin,
  Truck,
  ClipboardList,
  Package,
  DollarSign,
  FileText,
  Calendar,
  Filter,
  ChevronDown,
} from 'lucide-react';

// ── Date Filter Bar ────────────────────────────────────────────────────────────
const DateFilterBar = ({ filters, setFilters }) => {
  const [preset, setPreset] = useState('this_month');
  const applyPreset = (value) => {
    setPreset(value);
    const today = new Date();
    const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
    if (value === 'today') {
      setFilters((f) => ({ ...f, from_date: fmt(today), to_date: fmt(today) }));
    } else if (value === 'this_week') {
      const mon = new Date(today);
      mon.setDate(today.getDate() - today.getDay() + 1);
      setFilters((f) => ({ ...f, from_date: fmt(mon), to_date: fmt(today) }));
    } else if (value === 'this_month') {
      setFilters((f) => ({
        ...f,
        from_date: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
        to_date: fmt(today),
      }));
    } else if (value === 'this_year') {
      setFilters((f) => ({
        ...f,
        from_date: fmt(new Date(today.getFullYear(), 0, 1)),
        to_date: fmt(today),
      }));
    }
  };

  const presets = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'this_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 sm:p-5 mb-6">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-btn bg-brand flex items-center justify-center shrink-0">
          <Filter className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-semibold text-secondary">Filter by Date Range</span>
        <span className="sm:ml-auto text-xs text-gray-400 font-medium uppercase tracking-wide">
          Applied to all reports
        </span>
      </div>

      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
        <div className="flex flex-wrap gap-1.5 bg-gray-100 p-1 rounded-btn w-full sm:w-auto">
          {presets.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={preset === p.value ? 'primary' : 'ghost'}
              onClick={() => applyPreset(p.value)}
              className="rounded-btn"
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="hidden lg:block w-px h-8 bg-gray-200 shrink-0" />

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full lg:w-auto">
          <Calendar className="w-4 h-4 text-gray-400 hidden sm:block shrink-0" />
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => {
              setPreset('custom');
              setFilters((f) => ({ ...f, from_date: e.target.value }));
            }}
            className="input-clean w-full sm:w-auto text-sm py-2 min-w-[140px]"
          />
          <span className="text-xs text-gray-400 font-medium text-center sm:px-1">to</span>
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => {
              setPreset('custom');
              setFilters((f) => ({ ...f, to_date: e.target.value }));
            }}
            className="input-clean w-full sm:w-auto text-sm py-2 min-w-[140px]"
          />
        </div>
      </div>
    </div>
  );
};

// ── Color palette ──────────────────────────────────────────────────────────────
const colorMap = {
  indigo: { gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', shadow: 'rgba(99,102,241,0.22)' },
  teal:   { gradient: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', shadow: 'rgba(20,184,166,0.22)' },
  yellow: { gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', shadow: 'rgba(245,158,11,0.22)' },
  blue:   { gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)', shadow: 'rgba(59,130,246,0.22)' },
  orange: { gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)', shadow: 'rgba(37,99,235,0.22)' },
  rose:   { gradient: 'linear-gradient(135deg, #f43f5e, #fb7185)', shadow: 'rgba(244,63,94,0.22)' },
  purple: { gradient: 'linear-gradient(135deg, #a855f7, #c084fc)', shadow: 'rgba(168,85,247,0.22)' },
};

const buildLocationOptions = (locations) => {
  const seen = new Map();
  (locations || []).forEach((l) => {
    const label = (l.location || l.hub_name || l.name || l.location_name || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, {
        id: l.id || l.hub_id || label,
        value: label,
        label,
      });
    }
  });
  return Array.from(seen.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
};

// ── Report Card ────────────────────────────────────────────────────────────────
// extraFilters: array of JSX elements
//   length 0  → no filter section rendered
//   length 1  → single filter always visible
//   length 2+ → first filter always visible; rest slide open via arrow toggle with count badge
const ReportCard = ({ title, description, icon: Icon, color = 'indigo', extraFilters = [], onDownload, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const c = colorMap[color] || colorMap.indigo;

  const hasFilters = extraFilters.length > 0;
  const hasMore = extraFilters.length > 1;
  const firstFilter = extraFilters[0];
  const restFilters = extraFilters.slice(1);

  return (
    <div
      className="bg-white rounded-card border border-gray-100 p-5 sm:p-6 flex flex-col relative overflow-visible shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium"
    >
      {/* Top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px] rounded-t-card"
        style={{ background: c.gradient }}
      />

      {/* Card header */}
      <div className={`flex items-start gap-3.5 ${hasFilters ? 'mb-4' : 'mb-5'}`}>
        <div
          className="w-11 h-11 rounded-card flex items-center justify-center shrink-0"
          style={{ background: c.gradient, boxShadow: `0 4px 12px ${c.shadow}` }}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-secondary m-0 tracking-tight">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5 leading-snug">{description}</p>
        </div>
      </div>

      {/* Filter zone */}
      {hasFilters && (
        <div className="bg-gray-50 rounded-card border border-gray-100 p-3 mb-4 overflow-visible">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">{firstFilter}</div>

            {hasMore && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                title={expanded ? 'Hide filters' : `${restFilters.length} more filter${restFilters.length > 1 ? 's' : ''}`}
                className={`
                  relative shrink-0 w-8 h-8 rounded-btn border flex items-center justify-center
                  transition-all duration-200 cursor-pointer
                  ${expanded
                    ? 'border-transparent text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-brand hover:bg-brand-light'}
                `}
                style={expanded ? { background: c.gradient, boxShadow: `0 2px 8px ${c.shadow}` } : undefined}
              >
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180 text-white' : ''}`}
                />
                {!expanded && (
                  <span
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center border-2 border-white"
                    style={{ background: c.gradient, boxShadow: `0 1px 4px ${c.shadow}` }}
                  >
                    {restFilters.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {hasMore && (
            <div
              className={`overflow-hidden transition-all duration-300 ease-out ${
                expanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="flex flex-col gap-2 pt-1">
                {restFilters.map((f, i) => (
                  <div key={i}>{f}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Button
        onClick={onDownload}
        disabled={loading}
        loading={loading}
        icon={Download}
        className="mt-auto w-full"
      >
        {loading ? 'Generating…' : 'Download Excel'}
      </Button>
    </div>
  );
};

// ── Multi-Checkbox dropdown (portal so menu is not clipped by grid cards) ───────
const MultiCheckbox = ({ values, onChange, placeholder, options }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const menuMaxHeight = 260;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUpward = spaceBelow < Math.min(menuMaxHeight, 180) && spaceAbove > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 10050,
      top: openUpward ? undefined : rect.bottom + gap,
      bottom: openUpward ? window.innerHeight - rect.top + gap : undefined,
      maxHeight: openUpward
        ? Math.min(menuMaxHeight, spaceAbove)
        : Math.min(menuMaxHeight, spaceBelow),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) =>
    onChange(values.includes(val) ? values.filter((v) => v !== val) : [...values, val]);

  const menu = open ? (
    <div
      ref={menuRef}
      style={menuStyle}
      className="bg-white border border-gray-200 rounded-card shadow-medium overflow-y-auto"
    >
      {options.length === 0 && (
        <div className="px-3 py-2.5 text-xs text-gray-400">No options</div>
      )}
      {options.map((opt) => (
        <label
          key={opt.id || opt.value}
          className="flex items-center gap-2 px-3 py-2 hover:bg-brand-light cursor-pointer text-sm text-secondary"
        >
          <input
            type="checkbox"
            checked={values.includes(opt.value)}
            onChange={() => toggle(opt.value)}
            className="accent-brand w-3.5 h-3.5 cursor-pointer rounded"
          />
          {opt.label}
        </label>
      ))}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          if (next) setTimeout(updateMenuPosition, 0);
          setOpen(next);
        }}
        className="input-clean text-sm py-2 text-left flex justify-between items-center gap-2 cursor-pointer"
      >
        <span className={`truncate flex-1 ${values.length ? 'text-secondary' : 'text-gray-400'}`}>
          {values.length
            ? options.filter((o) => values.includes(o.value)).map((o) => o.label).join(', ')
            : placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {menu && createPortal(menu, document.body)}
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const Reports = () => {
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    from_date: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
    to_date: fmt(today),
  };
});

  const [shipmentFilters, setShipmentFilters] = useState({
    user_id: '', vehicle_id: '', payment_modes: [], shipment_client_id: '', shipment_id: '',
    sender_locations: [], receiver_locations: [], from_date: '', to_date: '',
  });
  const [vehicleFilter, setVehicleFilter]       = useState({ vehicle_id: '' });
  const [userFilter, setUserFilter]             = useState({ user_id: '' });
  const [locationFilter, setLocationFilter]     = useState({ location: '' });
  const [mlSheetFilter, setMlSheetFilter]       = useState({
    ml_sheet_id: '', sender_locations: [], receiver_locations: [],
  });
  const [onContractFilter, setOnContractFilter] = useState({ contract_id: '', vehicle_id: '' });
  const [commissionFilter, setCommissionFilter] = useState({ commission_id: '', ml_sheet_id: '' });

  const [users, setUsers]         = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients]     = useState([]);
  const [loadingMap, setLoadingMap] = useState({});

  useEffect(() => {
    const toArray = (d) => {
      if (Array.isArray(d)) return d;
      if (!d) return [];
      for (const key of ['users', 'vehicles', 'hubs', 'location_hubs', 'clients', 'data', 'results', 'items']) {
        if (Array.isArray(d[key])) return d[key];
        if (d.data && Array.isArray(d.data[key])) return d.data[key];
      }
      if (Array.isArray(d.data)) return d.data;
      return [];
    };
    userService.getAll(null, null, { all: true }).then((d) => setUsers(toArray(d))).catch(() => setUsers([]));
    vehicleService.getAllVehicles(null, null, { all: true }).then((d) => setVehicles(toArray(d))).catch(() => setVehicles([]));
    locationHubService.getAllHubs(null, null, { all: 'true' })
      .then((res) => {
        const data = res?.data ?? res;
        const hubs = data?.hubs ?? data?.location_hubs ?? toArray(data);
        setLocations(Array.isArray(hubs) ? hubs : []);
      })
      .catch(() => setLocations([]));
    shipmentClientService.getAll(null, null, { all: true }).then((d) => setClients(toArray(d))).catch(() => setClients([]));
  }, []);

  const locationOptions = useMemo(() => buildLocationOptions(locations), [locations]);

  const setLoading = (key, val) => setLoadingMap((prev) => ({ ...prev, [key]: val }));

  const handleDownload = async (key, downloadFn, params) => {
    setLoading(key, true);
    try {
      await downloadFn({ ...filters, ...params });
    } catch (err) {
      console.error(`Report error [${key}]:`, err);
      alert('Failed to generate report. Please try again.');
    } finally {
      setLoading(key, false);
    }
  };

  const baseInputStyle = 'input-clean text-sm py-2';

  const SelectField = ({ value, onChange, placeholder, options, labelKey = 'label', valueKey = 'value' }) => (
    <div className="relative w-full">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${baseInputStyle} pr-8 appearance-none cursor-pointer`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt[valueKey]} value={opt[valueKey]}>{opt[labelKey]}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
    </div>
  );

  const TextInput = ({ value, onChange, placeholder }) => (
    <input
      type="text" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={baseInputStyle}
    />
  );

  return (
    <PageLayout
      title="Reports"
      icon={FileSpreadsheet}
      actions={
        <div className="flex items-center gap-2 bg-brand-light border border-brand/10 rounded-card px-3 py-2">
          <div className="w-1.5 h-1.5 rounded-full bg-success" />
          <span className="text-xs font-semibold text-secondary">8 Reports Available</span>
        </div>
      }
    >
      <p className="text-sm text-gray-500 -mt-2 mb-6">Download Excel reports for any module</p>

                {/* Global date filter */}
                <DateFilterBar filters={filters} setFilters={setFilters} />

                {/* Report cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">

                  {/* 1. User Report — 1 filter, always visible */}
                  <ReportCard
                    title="User Report" description="All users filtered by role or city"
                    icon={Users} color="indigo" loading={!!loadingMap['user']}
                    extraFilters={[
                      <SelectField key="user" value={userFilter.user_id}
                        onChange={(v) => setUserFilter({ user_id: v })} placeholder="All Users"
                        options={(users || []).map((u) => ({
                          value: u.user_id || u.id,
                          label: `${u.full_name || u.name || ''} (${typeof u.role === 'string' ? u.role : u.role_name || u.role?.role_name || u.role?.name || ''})`,
                        }))}
                      />,
                    ]}
                    onDownload={() => handleDownload('user', reportService.downloadUserReport, userFilter)}
                  />

                  {/* 2. Location Report — 1 filter, always visible */}
                  <ReportCard
                    title="Location / Hub Report" description="All destination hubs and locations"
                    icon={MapPin} color="teal" loading={!!loadingMap['location']}
                    extraFilters={[
                      <SelectField key="loc" value={locationFilter.location}
                        onChange={(v) => setLocationFilter({ location: v })} placeholder="All Locations"
                        options={locationOptions}
                      />,
                    ]}
                    onDownload={() => handleDownload('location', reportService.downloadLocationReport, locationFilter)}
                  />

                  {/* 3. Vehicle Report — 1 filter, always visible */}
                  <ReportCard
                    title="Vehicle Report" description="Fleet data with insurance & RC status"
                    icon={Truck} color="yellow" loading={!!loadingMap['vehicle']}
                    extraFilters={[
                      <SelectField key="veh" value={vehicleFilter.vehicle_id}
                        onChange={(v) => setVehicleFilter({ vehicle_id: v })} placeholder="All Vehicles"
                        options={(vehicles || []).map((v) => ({
                          value: v.id || v.vehicle_id,
                          label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || v.number || ''}`,
                        }))}
                      />,
                    ]}
                    onDownload={() => handleDownload('vehicle', reportService.downloadVehicleReport, vehicleFilter)}
                  />

                  {/* 4. ML Sheet Report — first filter visible, +2 behind toggle with count badge */}
                  <ReportCard
                    title="ML Sheet Report" description="Master loading sheet data with shipment details"
                    icon={ClipboardList} color="blue" loading={!!loadingMap['mlsheet']}
                    extraFilters={[
                      <TextInput key="ml" value={mlSheetFilter.ml_sheet_id}
                        onChange={(v) => setMlSheetFilter((f) => ({ ...f, ml_sheet_id: v }))}
                        placeholder="ML Sheet ID (leave blank for all)"
                      />,
                      <MultiCheckbox key="ml_sender"
                        values={mlSheetFilter.sender_locations}
                        onChange={(v) => setMlSheetFilter((f) => ({ ...f, sender_locations: v }))}
                        placeholder="Sender Location (all)"
                        options={locationOptions}
                      />,
                      <MultiCheckbox key="ml_receiver"
                        values={mlSheetFilter.receiver_locations}
                        onChange={(v) => setMlSheetFilter((f) => ({ ...f, receiver_locations: v }))}
                        placeholder="Receiver Location (all)"
                        options={locationOptions}
                      />,
                    ]}
                    onDownload={() =>
                      handleDownload('mlsheet', reportService.downloadMLSheetReport, {
                        ml_sheet_id: mlSheetFilter.ml_sheet_id,
                        sender_location: mlSheetFilter.sender_locations.join(','),
                        receiver_location: mlSheetFilter.receiver_locations.join(','),
                      })
                    }
                  />

                  {/* 5. Shipment Report — 5 filters: first visible, +4 behind arrow */}
                  <ReportCard
                    title="Shipment Report" description="Shipments with payment totals, missing & returned sheets"
                    icon={Package} color="orange" loading={!!loadingMap['shipment']}
                    extraFilters={[
                      <TextInput key="sid" value={shipmentFilters.shipment_id}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, shipment_id: v }))}
                        placeholder="Shipment ID (leave blank for all)"
                      />,
                      <SelectField key="suser" value={shipmentFilters.user_id}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, user_id: v }))}
                        placeholder="All Agents / Users"
                        options={(users || []).map((u) => ({ value: u.user_id || u.id, label: u.full_name || u.name || '' }))}
                      />,
                      <SelectField key="sveh" value={shipmentFilters.vehicle_id}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, vehicle_id: v }))}
                        placeholder="All Vehicles"
                        options={(vehicles || []).map((v) => ({ value: v.id || v.vehicle_id, label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || ''}` }))}
                      />,
                      <MultiCheckbox key="spay"
                        values={shipmentFilters.payment_modes}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, payment_modes: v }))}
                        placeholder="All Payment Modes"
                        options={[
                          { value: 'paid',         label: 'Paid' },
                          { value: 'cash',         label: 'Cash' },
                          { value: 'to_pay',       label: 'To Pay' },
                          { value: 'credit',       label: 'Credit' },
                          { value: 'paid_balance', label: 'Paid Balance' },
                          { value: 'upi',          label: 'UPI' },
                          { value: 'bank_transfer',label: 'Bank Transfer' },
                          { value: 'cheque',       label: 'Cheque' },
                          { value: 'online',       label: 'Online' },
                          { value: 'foc',          label: 'FOC' },
                        ]}
                      />,
                      <SelectField key="sclient" value={shipmentFilters.shipment_client_id}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, shipment_client_id: v }))}
                        placeholder="All Clients / Vendors"
                        options={(clients || []).map((c) => ({ value: c.id || c.client_id, label: c.company_name || c.name || '' }))}
                      />,
                      <MultiCheckbox key="sloc"
                        values={shipmentFilters.sender_locations}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, sender_locations: v }))}
                        placeholder="From Location (all)"
                        options={locationOptions}
                      />,
                      <MultiCheckbox key="rloc"
                        values={shipmentFilters.receiver_locations}
                        onChange={(v) => setShipmentFilters((f) => ({ ...f, receiver_locations: v }))}
                        placeholder="To Location (all)"
                        options={locationOptions}
                      />,
                      <div key="date_row" className="flex items-center gap-2">
                        <input
                          type="date" value={shipmentFilters.from_date || ''}
                          onChange={(e) => setShipmentFilters((f) => ({ ...f, from_date: e.target.value }))}
                          className="input-clean flex-1 text-sm py-2 min-w-0"
                        />
                        <span className="text-xs text-gray-400 font-medium shrink-0">to</span>
                        <input
                          type="date" value={shipmentFilters.to_date || ''}
                          onChange={(e) => setShipmentFilters((f) => ({ ...f, to_date: e.target.value }))}
                          className="input-clean flex-1 text-sm py-2 min-w-0"
                        />
                      </div>,
                    ]}
                    onDownload={() => handleDownload('shipment', reportService.downloadShipmentReport, {
                      ...shipmentFilters,
                      payment_mode:      shipmentFilters.payment_modes.join(','),
                      sender_location:   shipmentFilters.sender_locations.join(','),
                      receiver_location: shipmentFilters.receiver_locations.join(','),
                    })}
                  />

                  {/* 6. Expense Report — no filters */}
                  <ReportCard
                    title="Expense Report" description="All company expenses filtered by date range"
                    icon={DollarSign} color="rose" loading={!!loadingMap['expense']}
                    extraFilters={[]}
                    onDownload={() => handleDownload('expense', reportService.downloadExpenseReport, {})}
                  />

                  {/* 7. On-Contract Report — 2 filters: first visible, +1 behind arrow */}
                  <ReportCard
                    title="On-Contract Report" description="Contract-wise P&L with ML sheet and shipment breakdown"
                    icon={FileText} color="purple" loading={!!loadingMap['oncontract']}
                    extraFilters={[
                      <TextInput key="cid" value={onContractFilter.contract_id}
                        onChange={(v) => setOnContractFilter((f) => ({ ...f, contract_id: v }))}
                        placeholder="Contract ID (leave blank for all)"
                      />,
                      <SelectField key="cveh" value={onContractFilter.vehicle_id}
                        onChange={(v) => setOnContractFilter((f) => ({ ...f, vehicle_id: v }))}
                        placeholder="All Vehicles"
                        options={(vehicles || []).map((v) => ({ value: v.id || v.vehicle_id, label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || ''}` }))}
                      />,
                    ]}
                    onDownload={() => handleDownload('oncontract', reportService.downloadOnContractReport, onContractFilter)}
                  />

                {/* 8. Commission Report — 2 filters: first visible, +1 behind arrow */}
                  <ReportCard
                    title="Commission Report" description="Shipment commissions with agent breakdown and payment history"
                    icon={DollarSign} color="teal" loading={!!loadingMap['commission']}
                    extraFilters={[
                      <TextInput key="comid" value={commissionFilter.commission_id}
                        onChange={(v) => setCommissionFilter((f) => ({ ...f, commission_id: v }))}
                        placeholder="Commission ID (leave blank for all)"
                      />,
                      <TextInput key="comml" value={commissionFilter.ml_sheet_id}
                        onChange={(v) => setCommissionFilter((f) => ({ ...f, ml_sheet_id: v }))}
                        placeholder="ML Sheet ID (leave blank for all)"
                      />,
                    ]}
                    onDownload={() => handleDownload('commission', reportService.downloadCommissionReport, commissionFilter)}
                  />

                </div>
    </PageLayout>
  );
};

export default Reports;